import { useMemo } from 'react'
import {
  WhatIfScenario,
  ScenarioImpactMetrics,
  MECETable,
  MECEUserState
} from '@models/index'
import { calculateScenarioMetrics } from '@utils/scenarioCalculator'
import './ScenarioImpactPanel.css'

interface ScenarioImpactPanelProps {
  scenario: WhatIfScenario
  meceTable: MECETable
  userState?: MECEUserState | null
  comparisonMetrics?: ScenarioImpactMetrics | null  // For comparison mode
  onClose?: () => void
}

export function ScenarioImpactPanel({
  scenario,
  meceTable,
  userState,
  comparisonMetrics,
  onClose
}: ScenarioImpactPanelProps) {
  const metrics = useMemo(
    () => calculateScenarioMetrics(scenario, meceTable, userState),
    [scenario, meceTable, userState]
  )

  const coveragePercent = metrics.total_tactics > 0
    ? Math.round((metrics.included_tactics / metrics.total_tactics) * 100)
    : 0

  // Calculate deltas if in comparison mode
  const deltas = comparisonMetrics ? {
    coverage: coveragePercent - (comparisonMetrics.total_tactics > 0
      ? Math.round((comparisonMetrics.included_tactics / comparisonMetrics.total_tactics) * 100)
      : 0),
    risk: metrics.risk_score - comparisonMetrics.risk_score,
    alignment: metrics.strategic_alignment_score - comparisonMetrics.strategic_alignment_score
  } : null

  const formatDelta = (value: number, inverse: boolean = false) => {
    if (value === 0) return null
    const isPositive = inverse ? value < 0 : value > 0
    return (
      <span className={`delta ${isPositive ? 'positive' : 'negative'}`}>
        {value > 0 ? '+' : ''}{value}
      </span>
    )
  }

  return (
    <div className="scenario-impact-panel">
      <div className="impact-header">
        <h4>Scenario Impact: {scenario.name}</h4>
        {onClose && (
          <button className="btn-close-impact" onClick={onClose} aria-label="Close impact panel">
            x
          </button>
        )}
      </div>

      {/* Main Metrics */}
      <div className="impact-metrics-grid">
        <div className="metric-card coverage">
          <div className="metric-value">
            {coveragePercent}%
            {deltas && formatDelta(deltas.coverage)}
          </div>
          <div className="metric-label">Coverage</div>
          <div className="metric-detail">
            {metrics.included_tactics}/{metrics.total_tactics} tactics
          </div>
        </div>

        <div className="metric-card risk">
          <div className="metric-value">
            {metrics.risk_score}
            {deltas && formatDelta(deltas.risk, true)}
          </div>
          <div className="metric-label">Risk Score</div>
          <div className="metric-detail">
            {metrics.risk_score < 30 ? 'Low' : metrics.risk_score < 60 ? 'Moderate' : 'High'}
          </div>
        </div>

        <div className="metric-card alignment">
          <div className="metric-value">
            {metrics.strategic_alignment_score}%
            {deltas && formatDelta(deltas.alignment)}
          </div>
          <div className="metric-label">Strategic Alignment</div>
          <div className="metric-detail">High-priority coverage</div>
        </div>
      </div>

      {/* Tactic Breakdown */}
      <div className="impact-section">
        <h5>Tactic Breakdown</h5>
        <div className="tactic-breakdown">
          <div className="breakdown-item included">
            <span className="breakdown-icon">✓</span>
            <span className="breakdown-count">{metrics.included_tactics}</span>
            <span className="breakdown-label">Included</span>
          </div>
          <div className="breakdown-item excluded">
            <span className="breakdown-icon">✗</span>
            <span className="breakdown-count">{metrics.excluded_tactics}</span>
            <span className="breakdown-label">Excluded</span>
          </div>
          <div className="breakdown-item maybe">
            <span className="breakdown-icon">?</span>
            <span className="breakdown-count">{metrics.maybe_tactics}</span>
            <span className="breakdown-label">Pending</span>
          </div>
        </div>
      </div>

      {/* Pillar Coverage */}
      <div className="impact-section">
        <h5>Coverage by Pillar</h5>
        <div className="pillar-coverage-list">
          {Object.entries(metrics.coverage_by_pillar).map(([pillarName, coverage]) => (
            <div key={pillarName} className="pillar-coverage-item">
              <div className="pillar-coverage-header">
                <span className="pillar-name">{pillarName}</span>
                <span className="pillar-percent">{coverage.percentage}%</span>
              </div>
              <div className="pillar-coverage-bar">
                <div
                  className="pillar-coverage-fill"
                  style={{ width: `${coverage.percentage}%` }}
                />
              </div>
              <div className="pillar-coverage-detail">
                {coverage.included}/{coverage.total} tactics
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* BSC Coverage */}
      <div className="impact-section">
        <h5>BSC Perspective Coverage</h5>
        <div className="bsc-coverage-grid">
          {Object.entries(metrics.bsc_coverage).map(([perspective, percent]) => (
            <div key={perspective} className="bsc-coverage-item">
              <div className="bsc-percent">{percent}%</div>
              <div className="bsc-name">{perspective}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Effort Estimate */}
      {(metrics.estimated_effort.low > 0 ||
        metrics.estimated_effort.medium > 0 ||
        metrics.estimated_effort.high > 0) && (
        <div className="impact-section">
          <h5>Estimated Effort (Included Tactics)</h5>
          <div className="effort-breakdown">
            <div className="effort-item low">
              <span className="effort-count">{metrics.estimated_effort.low}</span>
              <span className="effort-label">Low</span>
            </div>
            <div className="effort-item medium">
              <span className="effort-count">{metrics.estimated_effort.medium}</span>
              <span className="effort-label">Medium</span>
            </div>
            <div className="effort-item high">
              <span className="effort-count">{metrics.estimated_effort.high}</span>
              <span className="effort-label">High</span>
            </div>
          </div>
        </div>
      )}

      {/* Warning if many pending decisions */}
      {metrics.maybe_tactics > metrics.total_tactics * 0.3 && (
        <div className="impact-warning">
          <span className="warning-icon">⚠️</span>
          <span>{metrics.maybe_tactics} tactics still need a decision. Consider reviewing them to finalize your scenario.</span>
        </div>
      )}
    </div>
  )
}

export default ScenarioImpactPanel
